import { create } from 'zustand';

/**
 * Globaler Undo-Toast-Store. Lösch-Aktionen rufen `showUndo({...})` auf;
 * der UndoToast (im AppLayout) zeigt eine Snackbar mit "Rückgängig"-Knopf.
 * Klick auf den Knopf ruft `onUndo` auf (typischerweise eine Restore-API).
 * Läuft der Timer ab oder kommt ein neuer Toast nach, wird der alte
 * Toast einfach geschlossen — kein zusätzlicher Hard-Commit nötig, weil
 * der Server bereits beim Lösch-Klick auf Soft-Delete (deleted_at)
 * gesetzt hat.
 */
export interface UndoEntry {
  /** Eindeutige ID (für React-key und damit gleiche Undos nicht ankommen). */
  id: number;
  /** Menschen-lesbarer Text: z.B. "Aufgabe gelöscht". */
  label: string;
  /** Wird beim Klick auf "Rückgängig" aufgerufen. */
  onUndo: () => void;
  /** Anzeigedauer in Millisekunden (Default 7000). */
  ttlMs: number;
  /** Zeitpunkt, wann der Toast verschwinden soll. */
  expiresAt: number;
}

interface UndoState {
  current: UndoEntry | null;
  /** Zeigt einen neuen Undo-Toast. Vorhandener Toast wird sofort
   *  ausgeblendet (Soft-Delete bleibt bestehen). */
  show: (entry: { label: string; onUndo: () => void; ttlMs?: number }) => void;
  /** Klick auf "Rückgängig" — ruft onUndo auf und schliesst. */
  trigger: (id: number) => void;
  /** Toast schliessen ohne Undo (z.B. Timer abgelaufen). */
  dismiss: (id: number) => void;
}

let nextId = 1;

export const useUndoStore = create<UndoState>((set, get) => ({
  current: null,
  show: ({ label, onUndo, ttlMs = 7000 }) => {
    const id = nextId++;
    set({
      current: {
        id,
        label,
        onUndo,
        ttlMs,
        expiresAt: Date.now() + ttlMs,
      },
    });
  },
  trigger: (id) => {
    const cur = get().current;
    if (cur && cur.id === id) {
      cur.onUndo();
      set({ current: null });
    }
  },
  dismiss: (id) => {
    const cur = get().current;
    if (cur && cur.id === id) set({ current: null });
  },
}));
